import React, { useState } from 'react'
import { LocationNode } from '../gql/graphql'
import { useLocalStorage } from 'usehooks-ts'

export type ReducedLocationNode = Pick<
  LocationNode,
  'id' | 'name' | 'formattedAddress'
>

interface BusinessLocationContextData {
  availableLocations: ReducedLocationNode[]
  selectedLocation?: ReducedLocationNode
  setLocation: (location: ReducedLocationNode) => void
  setAvailableLocations: (locations: ReducedLocationNode[]) => void
  clearLocation: () => void
}

const BusinessLocationContext =
  React.createContext<BusinessLocationContextData>({
    availableLocations: [],
    selectedLocation: undefined,
    setLocation: () => {},
    setAvailableLocations: () => {},
    clearLocation: () => {},
  })

interface BusinessLocationProviderProps {
  children?: React.ReactNode
}

export function useBusinessLocation() {
  const context = React.useContext(BusinessLocationContext)

  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider')
  }

  return context
}

export const BusinessLocationProvider: React.FC<
  BusinessLocationProviderProps
> = ({ children }) => {
  const [availableLocations, setAvailableLocations] = useState<
    ReducedLocationNode[]
  >([])
  const [location, setLocation] = useLocalStorage<
    ReducedLocationNode | undefined
  >('location', undefined)

  return (
    <BusinessLocationContext.Provider
      value={{
        availableLocations,
        selectedLocation: location,
        setLocation,
        clearLocation: () => setLocation(undefined),
        setAvailableLocations,
      }}
    >
      {children}
    </BusinessLocationContext.Provider>
  )
}
